import React, { useState, useCallback, useEffect } from "react";
import cn from "classnames";
import Dropzone from "react-dropzone";
import { useFormikContext, useField } from "formik";
import { useTranslation } from "react-i18next";

import {
    DEFAULT_FILE_FORMATS,
    FILE_SIZE,
    INVALID_FILE_ERROR,
    LARGE_FILE_ERROR,
} from "constants/files";

import InputError, { ERROR_PLACE } from "components/InputsField/InputError";
import FileIcon from "components/common/FileIcon";
import Label from "components/InputsField/Label";
import { showErrorMessage } from "helpers/notifications";

import styles from "./styles.module.scss";

export default function FormikDropzoneField({
    name,
    filesCount,
    classes,
    fileFormats,
    label,
    require,
}) {
    const [field, meta] = useField(name);
    const { setFieldValue } = useFormikContext();
    const [files, setFiles] = useState(field.value || []);
    const { t } = useTranslation();

    const onDrop = useCallback(
        (acceptedFiles) => {
            if (files.length < filesCount && files.length + acceptedFiles.length <= filesCount) {
                setFieldValue(name, [...files, ...acceptedFiles]);
                setFiles([...files, ...acceptedFiles]);
            } else {
                const firstFiles = [...files, ...acceptedFiles].slice(0, filesCount);
                setFieldValue(name, firstFiles);
                setFiles(firstFiles);
            }
        },
        [files, filesCount, name, setFieldValue],
    );

    useEffect(() => {
        field.value ? setFiles(field.value) : setFiles([]);
    }, [field.value]);

    const onDeleteClick = (index) => {
        setFieldValue(name, [...files.slice(0, index), ...files.slice(index + 1)]);
        setFiles([...files.slice(0, index), ...files.slice(index + 1)]);
    };

    const fileList = files.map((file, index) => (
        <li className={cn(styles.item, classes)} key={`${file.name}-${index}`}>
            <span className="file__name">{file.name}</span>
            <button className="file__delete" onClick={() => onDeleteClick(index)} type="button" />
        </li>
    ));

    const handleDropReject = useCallback(
        (e) => {
            if (e[0].errors.find((error) => error.code === LARGE_FILE_ERROR)) {
                showErrorMessage(t("errors.fileSize"));
            }
            if (e[0].errors.find((error) => error.code === INVALID_FILE_ERROR)) {
                showErrorMessage(t("errors.fileExtension"));
            }
        },
        [t],
    );

    return (
        <>
            {label && <Label label={label} name={name} require={require} />}
            <Dropzone
                onDrop={onDrop}
                name={name}
                maxSize={FILE_SIZE}
                onDropRejected={handleDropReject}
                accept={fileFormats || DEFAULT_FILE_FORMATS}
            >
                {({ getRootProps, getInputProps }) => (
                    <div className={styles.dropzone}>
                        {files.length < filesCount && (
                            <div
                                className={cn(styles.input, {
                                    [styles["input-warn"]]: meta.touched && meta.error,
                                })}
                                {...getRootProps()}
                            >
                                <input
                                    {...getInputProps()}
                                    accept={fileFormats || DEFAULT_FILE_FORMATS}
                                />
                                <FileIcon />
                                <span>{t("addFile")}</span>
                            </div>
                        )}
                        <ul>{fileList}</ul>
                    </div>
                )}
            </Dropzone>
            {meta.touched && meta.error ? (
                <InputError message={meta.error} mode={ERROR_PLACE.static} />
            ) : null}
        </>
    );
}
